import type { AxiosRequestConfig } from 'axios';

export const requestRejectedMessage = 'Не удалось загрузить файл: запрос отклонён защитой сервера. Обратитесь в поддержку.';

export class RequestRejectedError extends Error {
    constructor(readonly supportId?: string) {
        super(requestRejectedMessage);
        this.name = 'RequestRejectedError';
    }
}

export function withUploadRejectionDetection(config?: AxiosRequestConfig): AxiosRequestConfig & { detectUploadRejection: true } {
    return { ...config, detectUploadRejection: true };
}

export function hasEmbeddedDataImage(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    const tags = /<!--[\s\S]*?(?:-->|$)|<\/?[a-z][a-z\d:-]*(?:[^<>"']|"[^"]*"|'[^']*')*>/gi;
    let rawTextTag: string | undefined;
    for (const match of value.matchAll(tags)) {
        const tag = match[0];
        if (tag.startsWith('<!--')) continue;
        const name = /^<(\/?)([a-z][a-z\d:-]*)/i.exec(tag);
        if (!name) continue;
        const closing = name[1] === '/';
        const tagName = name[2].toLowerCase();
        if (rawTextTag) {
            if (closing && tagName === rawTextTag) rawTextTag = undefined;
            continue;
        }
        if (closing) continue;
        if (tagName === 'plaintext') return false;
        if (/^(?:script|style|textarea|title|xmp|iframe|noembed|noframes)$/.test(tagName)) {
            rawTextTag = tagName;
            continue;
        }
        if (tagName !== 'img') continue;
        const attributes = /\s+([^\s"'<>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
        for (const attribute of tag.slice(name[0].length).matchAll(attributes)) {
            if (attribute[1].toLowerCase() !== 'src') continue;
            const source = attribute[2] ?? attribute[3] ?? attribute[4] ?? '';
            // проверяем только префикс адреса, не декодируя содержимое изображения
            if (/^data:image\//i.test(decodeEntities(source.trim().slice(0, 256)).trim())) return true;
            break;
        }
    }
    return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function decodeEntities(value: string): string {
    const named: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
    return value.replace(/&(?:#(\d{1,7})|#x([\da-f]{1,6})|(amp|lt|gt|quot|apos|nbsp));/gi,
        (entity: string, decimal: string | undefined, hex: string | undefined, name: string | undefined) => {
            if (name) return named[name.toLowerCase()];
            const code = decimal ? Number(decimal) : parseInt(hex ?? '', 16);
            return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff)
                ? String.fromCodePoint(code) : entity;
        });
}

function extractSupportId(html: string, truncated: boolean): string | undefined {
    // убираем только оформление, сохраняя идентификатор в угловых скобках
    const text = decodeEntities(decodeEntities(html))
        .replace(/<\/?(?:b|strong|span|em|i|small|font|code|pre|p|div|br)(?=[\s/>])[^>]*>/gi, ' ');
    const label = /\bsupport\s+ID\b\s*(?:is\b\s*)?:?\s*/i.exec(text);
    if (!label) return undefined;
    const value = text.slice(label.index + label[0].length);
    const match = /^(?:<([a-z\d_-]{1,128})>|([a-z\d_-]{1,128}))(?=\s*(?:<|$))/i.exec(value);
    if (!match || (truncated && !value.slice(match[0].length).trim())) return undefined;
    const id = match[1] ?? match[2];
    const htmlTags = 'a abbr acronym address applet area article aside audio base basefont bdi bdo big blockquote body button canvas caption center col colgroup data datalist dd del details dfn dialog dir dl dt embed fieldset figcaption figure footer form frame frameset h1 h2 h3 h4 h5 h6 head header hgroup hr html iframe img input ins kbd label legend li link main map mark marquee menu meta meter nav noembed noframes noscript object ol optgroup option output param picture plaintext portal progress q rb rp rt rtc ruby s samp script search section select slot source strike style sub summary sup svg table tbody td template textarea tfoot th thead time title tr track tt u ul var video wbr xmp';
    if (match[1] && htmlTags.split(' ').includes(id.toLowerCase())) return undefined;
    return id;
}

export function getUploadRejection(response: unknown): RequestRejectedError | undefined {
    if (!isRecord(response) || !isRecord(response.config) || response.config.detectUploadRejection !== true
        || typeof response.data !== 'string') return undefined;

    const limit = 64 * 1024;
    const prefix = response.data.slice(0, limit);
    const bytes = new TextEncoder().encode(prefix);
    const truncated = prefix.length < response.data.length || bytes.length > limit;
    const html = new TextDecoder().decode(bytes.subarray(0, limit));
    if (!/^\s*(?:<!doctype\s+html[^>]*>\s*)?<html(?=[\s/>])/i.test(html)
        || !/<title(?=[\s/>])[^>]*>\s*Request Rejected\s*<\/title\s*>/i.test(html)
        || !/<body(?=[\s/>])[^>]*>[\s\S]*?The\s+requested\s+URL\s+was\s+rejected\./i.test(html)) return undefined;

    return new RequestRejectedError(extractSupportId(html, truncated));
}
